import { Component, OnInit, ViewChild } from '@angular/core';
import { AbstractControl, FormBuilder, FormGroup, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { ActivatedRoute, Router, NavigationExtras } from '@angular/router';
import { Location } from '@angular/common';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ClientForCreation } from '../../../../core/models/clients/client-for-creation.model';
import { ClientForUpdation } from '../../../../core/models/clients/client-for-updation.model';
import { ClientService } from '../../../../core/services/client.service';
import { BlobOverviewDto } from '../../../../core/models/blobs/blob-overview.model';
import { FileUploaderComponent } from '../../../../shared/components/file-uploader/file-uploader.component';
import { AttachmentForCreation } from '../../../../core/models/attachments/attachment-for-creation.model';

@Component({
  standalone: false,
  selector: 'app-client-form',
  templateUrl: './client-form.component.html',
  styleUrls: ['./client-form.component.css']
})
export class ClientFormComponent implements OnInit {
  clientForm!: FormGroup;
  isEditMode = false;
  isSubmited = false;
  clientId!: number;

  @ViewChild(FileUploaderComponent) fileUploader!: FileUploaderComponent;

  private returnUrl: string | null = null;

  constructor(
    private fb: FormBuilder,
    private clientService: ClientService,
    private route: ActivatedRoute,
    private router: Router,
    private location: Location,
    private snackBar: MatSnackBar
  ) { }

  /**
   * Validator for optional email field
   * Returns null if empty (valid) or if email format is valid
   * Returns error if email format is invalid
   */
  private optionalEmailValidator: ValidatorFn = (control: AbstractControl): ValidationErrors | null => {
    if (!control.value || (typeof control.value === 'string' && control.value.trim() === '')) {
      return null; // Empty is valid (optional field)
    }
    return Validators.email(control);
  }

  ngOnInit(): void {
    // Build form with fields that match our DTOs
    this.clientForm = this.fb.group({
      fullName: ['', Validators.required],
      email: ['', this.optionalEmailValidator], // Email is optional but must be valid if provided
      phoneNumber: ['', Validators.required],
      address: ['', Validators.required]
    });

    // Get returnUrl from router state, history state, or query params
    const navigation = this.router.getCurrentNavigation();
    if (navigation?.extras?.state?.['returnUrl']) {
      this.returnUrl = navigation.extras.state['returnUrl'];
    } else if ((window.history.state as any)?.returnUrl) {
      this.returnUrl = (window.history.state as any).returnUrl;
    } else {
      this.route.queryParams.subscribe(params => {
        if (params['returnUrl']) {
          this.returnUrl = params['returnUrl'];
        }
      });
    }

    // Check for edit mode using route parameters
    this.route.params.subscribe(params => {
      if (params['id']) {
        this.isEditMode = true;
        this.clientId = +params['id'];
        this.clientService.getClientById(this.clientId).subscribe(client => {
          // Patch the form with existing client data
          this.clientForm.patchValue({
            fullName: client.fullName,
            email: client.email,
            phoneNumber: client.phoneNumber,
            address: client.address
          });
        });
      }
    });
  }

  private navigateBack(): void {
    if (this.returnUrl) {
      this.router.navigateByUrl(this.returnUrl);
    } else {
      // Try to go back in history, otherwise default to listing
      if (window.history.length > 1) {
        this.location.back();
      } else {
        this.router.navigate(['/clients']);
      }
    }
  }

  // google drive file uploads
  // onFilesUploaded(paths: string[]) {
  //   console.log('Google Drive File Paths:', paths);
  //   alert('Files uploaded successfully! Check console for paths. '+ JSON.stringify(paths));
  //   // Call .NET backend API to save these
  // //  this.http.post('/api/documents/save', { filePaths: paths }).subscribe(...);
  // }

  async submitForm(): Promise<void> {
    // Mark all fields as touched to show validation errors
    Object.keys(this.clientForm.controls).forEach(key => {
      this.clientForm.get(key)?.markAsTouched();
    });

    if (this.clientForm.invalid) {
      // Keep button enabled if validation fails so user can fix and retry
      this.isSubmited = false;
      return;
    }

    // Only disable button when validation passes and we're submitting
    this.isSubmited = true;
    
    if (this.isEditMode) {
      const updatedClient: ClientForUpdation = { id: this.clientId, ...this.clientForm.value };
      this.clientService.updateClient(updatedClient).subscribe({
        next: () => {
          this.snackBar.open('Client updated successfully', 'Close', { duration: 3000 });
          this.navigateBack();
        },
        error: () => {
          this.snackBar.open('Failed to update client', 'Close', { duration: 3000 });
          this.isSubmited = false;
        }
      });
    } else {
      // Upload files before submitting client (only for new clients)
      const uploadedFiles = await this.fileUploader.uploadAllFiles();
      const attachments: AttachmentForCreation[] = uploadedFiles.map(file => ({
        blobId: file.id,
        attachmentType: 'Client'
      }));
      const newClient: ClientForCreation = { ...this.clientForm.value, attachments };
      this.clientService.createClient(newClient).subscribe({
        next: () => {
          this.snackBar.open('Client added successfully', 'Close', { duration: 3000 });
          this.navigateBack();
        },
        error: () => {
          this.snackBar.open('Failed to add client', 'Close', { duration: 3000 });
          this.isSubmited = false;
        }
      });
    }
  }
}
